// functions for creating kiddom nodes
// to be used when editing an existing kiddom tree

import { NodeBodyKeys, NodeType } from 'shared/types/ingestion'
import { INode } from 'shared/types/tree'
import { childNodesOfType } from '../utility/TreeHelpers'
import { idsForNode, UpdateMethod, UpdateType } from './nodeUpdates/utility'
import { UpdateData } from './nodeUpdates/types'

export function createInlineNode(nodes: { type: string }[]) {
  return {
    type: NodeType.Inline,
    body: {
      type: nodes[0].type,
    },
    nodes,
  }
}

export function createQuestionAnswerList() {
  const questionAnswer = {
    type: NodeType.QuestionAnswer,
    body: { value: '' },
  }
  return createInlineNode([questionAnswer])
}

export function createQuestionPlaceholder({
  externalId,
  value,
}: {
  externalId: string
  value: string
}) {
  return {
    type: NodeType.QuestionPlaceholder,
    body: {
      [NodeBodyKeys.ExternalID]: externalId,
      [NodeBodyKeys.Answer]: [value],
    },
  }
}

export function createNodeList(title: string, nodes: { type: string }[]) {
  return {
    type: NodeType.NodeList,
    title,
    body: {
      type: nodes[0].type,
    },
    nodes,
  }
}

export function createNote({
  title,
  value,
  studentFacing,
}: {
  title: string
  value: string
  studentFacing: boolean
}) {
  return {
    type: NodeType.Note,
    title,
    body: {
      studentFacing,
      value,
    },
  }
}

function createPresentationAttachmentLeaf(presentation: Presentation) {
  const fileParts = presentation.kiddomLink.split('/')
  fileParts.pop()
  const fileIdPart = fileParts.pop()
  return {
    type: 'content:google',
    body: {
      type: NodeType.Document,
      is_ingested: true,
      can_not_copy_presentation: false,
      file_id: fileIdPart,
      file: {
        name: presentation.presentationTitle,
        type: 'application/vnd.google-apps.presentation',
        url: presentation.kiddomLink,
      },
    },
  }
}

function createPresentationAttachment(presentation: Presentation) {
  // This assumes the file url looks like
  // https://docs.google.com/presentation/d/FILE_ID/edit?usp=sharing
  // if the url does not have the last we will not get the correct file id
  return {
    type: NodeType.AttachmentsList,
    title: 'Presentation',
    body: {
      studentFacing: true,
    },
    leaves: [createPresentationAttachmentLeaf(presentation)],
  }
}

export interface Presentation {
  description: string
  kiddomLink: string
  presentationTitle: string
}

export function createPresentation(
  presentation: Presentation,
  listTitle = 'Presentations',
) {
  const presentationNode = {
    title: presentation.presentationTitle,
    body: {
      isAssignable: true,
      studentFacing: true,
    },
    type: NodeType.Presentation,
    nodes: [
      createNote({
        title: 'Description',
        value: presentation.description,
        studentFacing: true,
      }),
      createPresentationAttachment(presentation),
    ],
  }
  return createNodeList(listTitle, [presentationNode])
}

export function presentationUpdates(
  presentationNode: INode,
  presentationData: Presentation,
) {
  const nodeUpdates: UpdateData[] = []
  const [noteNode] = childNodesOfType(presentationNode, NodeType.Note)
  if (!noteNode) {
    nodeUpdates.push({
      type: UpdateType.node,
      ids: idsForNode(presentationNode),
      params: createNote({
        title: 'Description',
        value: presentationData.description,
        studentFacing: true,
      }),
      method: UpdateMethod.create,
    })
  } else if (noteNode.body?.value !== presentationData.description) {
    nodeUpdates.push({
      type: UpdateType.node,
      ids: idsForNode(noteNode),
      params: {
        body: {
          value: presentationData.description,
          studentFacing: true,
        },
      },
      method: UpdateMethod.update,
    })
  }
  const [attachmentNode] = childNodesOfType(
    presentationNode,
    NodeType.AttachmentsList,
  )
  if (!attachmentNode) {
    nodeUpdates.push({
      type: UpdateType.node,
      ids: idsForNode(presentationNode),
      params: createPresentationAttachment(presentationData),
      method: UpdateMethod.create,
    })
  } else {
    const leaf = attachmentNode.leaves?.[0]
    if (!leaf) {
      nodeUpdates.push({
        type: UpdateType.leaf,
        ids: idsForNode(attachmentNode),
        params: createPresentationAttachmentLeaf(presentationData),
        method: UpdateMethod.create,
      })
    } else if (leaf.body?.file?.url !== presentationData.kiddomLink) {
      const { body } = createPresentationAttachmentLeaf(presentationData)
      nodeUpdates.push({
        type: UpdateType.leaf,
        ids: idsForNode(leaf),
        params: { body },
        method: UpdateMethod.update,
      })
    }
  }
  return nodeUpdates
}
